import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';
import { Button } from '@/components/ui/button';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildFAQSchema } from '@/lib/schema';
import OpeningCatalog from '@/components/OpeningCatalog';
import HowItWorks5 from '@/components/HowItWorks5';

// Single source of truth for FAQ. Drives both the visible content
// rendered inside the FAQ section AND the FAQPage JSON-LD. If you
// edit visible Q&A here, the schema stays in sync automatically.
const FAQ_ITEMS = [
  {
    question: 'Do I need a permit for impact windows in Miami-Dade?',
    answer: 'Yes. All impact window installations in Miami-Dade County require a building permit. We handle the entire permit process, including application, submission, and inspection coordination.',
  },
  {
    question: 'How long does installation take?',
    answer: 'The entire process from contract to completion takes 8–12 weeks. Physical installation typically takes 1–3 days. Most of the timeline is permit approval (3–7 days) and product manufacturing (4–10 weeks).',
  },
  {
    question: 'Can I replace windows in a condo building in Miami?',
    answer: 'Yes. However, condo and HOA projects require board approval before installation. We handle all HOA coordination, product submittals, and building compliance requirements. Expect to add 2–4 weeks for HOA approval.',
  },
  {
    question: 'Will impact windows lower my insurance in Miami?',
    answer: "Yes. Most homeowners see 10–45% premium reductions after completing a wind mitigation inspection. Average savings are 20–30% annually. You'll need the permit sign-off and installation certificates to claim the discount.",
  },
  {
    question: 'What affects the price of impact windows in Miami?',
    answer: 'Price depends on: (1) Product tier (Good/Better/Premium), (2) Number and size of openings, (3) Installation complexity (multi-story, waterfront access), (4) HOA or building requirements, and (5) Geographic location within Miami-Dade (permit fees vary by city).',
  },
];

export default function ImpactWindowsMiami() {
  const pageData = {
    title: "Impact Windows in Miami: Pricing, Permits, and Installation",
    metaDescription: "Impact windows in Miami: real pricing, permit requirements, and installation timelines. Get a photo-based quote — no in-home appointment required.",
    canonicalPath: "/impact-windows-miami",
    
    directAnswer: (
      <>
        <p className="text-lg text-[var(--brand-slate-700)] mb-4 leading-relaxed">
          Most companies require an in-home visit just to give you pricing. We don't. Upload photos and receive clear pricing with Good, Better, and Premium options.
        </p>
        <p>
          Impact window installation in Miami and Miami-Dade County requires <strong>Miami-Dade NOA certification</strong>, building permits, and High Velocity Hurricane Zone (HVHZ) compliance. Most residential projects cost <strong>$15,000–$40,000</strong> depending on property size and product tier. Installation timelines average <strong>8–12 weeks</strong> from contract to completion, including permit approval and manufacturing.
        </p>
      </>
    ),

    sections: [
      {
        title: "Who This Is For",
        testId: "who-this-is-for",
        bullets: [
          "Homeowners comparing impact window options and pricing",
          "Condo owners dealing with HOA approval and building requirements",
          "People who want pricing without a sales appointment",
          "Anyone looking for clear, transparent Good/Better/Premium options"
        ]
      },

      {
        title: "How Snap Windows Is Different",
        testId: "how-different",
        bullets: [
          "No in-home appointment required — upload photos and receive pricing online",
          "Good / Better / Premium comparison — choose what fits your budget",
          "Transparent pricing — no games, no pressure",
          "Full project management — we handle permits, HOA approvals, and inspections"
        ],
        highlight: "See how real Miami homeowners chose their options on our case studies page."
      },
      {
        title: "Why Miami Homeowners Choose Impact Windows",
        testId: "why-miami",
        content: (
          <p className="mb-4">
            Miami's coastal location and hurricane exposure make impact windows essential for protection and insurance savings. Whether you own a waterfront condo in Brickell or Aventura, or a single-family home in Kendall or Coral Gables, impact windows are now standard across Miami-Dade County:
          </p>
        ),
        bullets: [
          "Hurricane Protection: Miami is in the High Velocity Hurricane Zone (HVHZ), requiring impact-rated products for all new construction and most upgrades",
          "Insurance Savings: Wind mitigation inspections after impact window installation reduce premiums by 10–45% annually",
          "Noise Reduction: Laminated glass blocks street noise, boat traffic, and aircraft sound — critical for urban and waterfront properties in areas like Sunny Isles, Brickell, and Miami Beach",
          "Energy Efficiency: Low-E coatings reduce cooling costs by 15–25%, essential in Miami's year-round heat",
          "Property Value: Homes with impact windows sell faster and command higher prices in Miami's competitive market",
          "Older Home Upgrades: Many 1970s–1990s homes in Kendall, Westchester, and Coral Gables are now upgrading from original aluminum windows to impact-rated systems"
        ],
        highlight: "Miami-Dade has the strictest building codes in the U.S. All impact windows must carry a Miami-Dade NOA (Notice of Acceptance) to pass inspection. Learn more about the installation process."
      },

      {
        title: "Miami-Dade Permit Requirements",
        testId: "permits",
        content: (
          <div className="space-y-4">
            <p className="mb-4">
              All impact window installations in Miami-Dade County require building permits. We recently worked on a condo project in North Miami where the building required specific manufacturer approval and coastal-grade finishes — we handled the entire HOA and permit coordination process.
            </p>
          </div>
        ),
        bullets: [
          "Permit Application: Must include product NOAs, installation plans, and contractor license verification",
          "Approval Timeline: 3–7 business days for standard residential projects (can extend to 2–4 weeks for complex cases)",
          "Permit Fees: Typically $300–$700 depending on project scope",
          "Inspection Required: City inspector verifies HVHZ compliance and installation quality before final sign-off",
          "Wind Mitigation Documentation: Permit sign-off is required to claim insurance discounts"
        ],
        highlight: "We handle all permit preparation, submission, and inspection coordination. You don't need to visit the building department. See our full installation timeline."
      },

      {
        title: "Pricing for Miami Homes",
        testId: "pricing",
        table: {
          headers: ["Property Type", "Typical Openings", "Estimated Cost Range"],
          rows: [
            ["Small Condo/Townhome", "6–10 windows", "$12,000 – $18,000"],
            ["Medium Condo/Townhome", "10–15 windows", "$18,000 – $28,000"],
            ["Standard Single-Family Home", "12–18 windows + 2–3 doors", "$20,000 – $35,000"],
            ["Large Single-Family Home", "18–25 windows + 3–4 doors", "$30,000 – $50,000"],
            ["Waterfront or Estate Home", "25+ windows + 4+ doors", "$50,000 – $100,000+"]
          ]
        },
        content: (
          <p className="mt-4 mb-4">
            Pricing includes product, installation labor, permits, old window removal, and final inspection coordination.
          </p>
        ),
        highlight: "Every home is different. We provide Good, Better, and Premium options so you can choose what fits your budget and goals. See real pricing examples in our case studies."
      },

      {
        title: "Areas We Serve in Miami-Dade",
        testId: "areas-served",
        content: (
          <div className="mb-4">
            <p className="mb-4">We serve all of Miami-Dade County, including:</p>
            <div className="grid md:grid-cols-3 gap-3">
              <ul className="list-disc pl-6 space-y-1 text-sm text-[var(--brand-slate-700)]">
                <li>Miami</li>
                <li>Coral Gables</li>
                <li>Kendall</li>
                <li>Doral</li>
                <li>North Miami</li>
              </ul>
              <ul className="list-disc pl-6 space-y-1 text-sm text-[var(--brand-slate-700)]">
                <li>Aventura</li>
                <li>Homestead</li>
                <li>Miami Beach</li>
                <li>Pinecrest</li>
                <li>South Miami</li>
              </ul>
              <ul className="list-disc pl-6 space-y-1 text-sm text-[var(--brand-slate-700)]">
                <li>Cutler Bay</li>
                <li>Palmetto Bay</li>
                <li>Key Biscayne</li>
                <li>Coconut Grove</li>
                <li>Brickell</li>
              </ul>
            </div>
          </div>
        )
      },

      {
        title: "How It Works",
        testId: "process",
        bullets: [
          "Upload Photos: Submit photos of your home through our online form",
          "Receive Pricing: Get Good, Better, and Premium options within 24 hours (no in-home appointment required)",
          "Choose Your Tier: Compare options and select what works for your home and budget",
          "Permit & Order: We handle all permit paperwork and place your order",
          "Installation: Licensed crew completes installation in 1–3 days",
          "Final Inspection: City inspector approves work and you receive documentation for insurance discounts"
        ],
        highlight: "Total timeline: 8–12 weeks from contract to completion. Most of that time is permit approval and product manufacturing."
      },

      {
        title: "Frequently Asked Questions",
        testId: "faq",
        content: (
          <div className="space-y-6">
            {FAQ_ITEMS.map(({ question, answer }, i) => (
              <div key={i}>
                <p className="font-semibold text-[var(--brand-slate-900)] mb-2">{question}</p>
                <p className="text-[var(--brand-slate-700)]">{answer}</p>
              </div>
            ))}
          </div>
        )
      }
    ],

    relatedLinks: [
      {
        title: "Get Your Quote",
        description: "Upload photos and receive pricing",
        url: "/get-quote?src=miami"
      },
      {
        title: "See Real Projects",
        description: "Miami homeowners' case studies",
        url: "/case-studies"
      },
      {
        title: "Installation Process",
        description: "Step-by-step timeline",
        url: "/installation-process"
      },
      {
        title: "Insurance Savings",
        description: "Wind mitigation explained",
        url: "/insurance-savings"
      }
    ],

    ctaText: "Get My Price",
    ctaSubtext: "Upload photos and receive Good, Better, and Premium installed pricing for your Miami home. No in-home appointment required. No pressure. Just clear pricing.",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Impact Windows Miami" }
    ]
  };

  return (
    <>
      <SchemaMarkup
        schemas={buildFAQSchema(FAQ_ITEMS)}
        instanceId="impact-windows-miami-faq"
      />
      <ContentPageTemplate {...pageData} />
      {/* Productized refresh: compact catalog + how-it-works appended below the
          template's content, before its footer. Additive only — does not touch
          the template's SEO content or schema. */}
      <OpeningCatalog src="impact_windows_miami_catalog" variant="compact" />
      <HowItWorks5 src="impact_windows_miami_how_it_works" variant="compact" />
    </>
  );
}
